import React, { useState, useEffect } from 'react';
import { logSessionStart, logSessionEnd } from '../services/logService';
import { AdminLogButton } from './AdminLogButton';

interface LoginGuardProps {
    children: React.ReactNode;
}

export const LoginGuard: React.FC<LoginGuardProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(false);
    const [isWaking, setIsWaking] = useState(true);

    // Check if session is already active
    useEffect(() => {
        const session = sessionStorage.getItem('nd_auth_session');
        const name = sessionStorage.getItem('nd_inspector_name');
        const email = sessionStorage.getItem('nd_inspector_email');

        if (session === 'active' && name && email) {
            setIsAuthenticated(true);
            // If IP is missing, try to fetch it again (it might have failed last time)
            if (!sessionStorage.getItem('nd_inspector_ip')) {
                fetchIp();
            }
        } else if (session === 'active') {
            // Session active but missing data - force re-login
            sessionStorage.clear();
            setIsAuthenticated(false);
        }

        setTimeout(() => setIsWaking(false), 500); // Intro fade

        // Handle session termination on window close
        const handleUnload = () => {
            logSessionEnd();
        };

        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, []);

    const fetchIp = async () => {
        let userIp = "Unknown";
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2500);

            // Try primary and secondary IP providers for better reliability
            const response = await fetch('https://api.ipify.org?format=json', { signal: controller.signal })
                .catch(() => fetch('https://ipapi.co/json/', { signal: controller.signal }));

            const data = await response.json();
            clearTimeout(timeoutId);
            userIp = data.ip || "Unknown";
            sessionStorage.setItem('nd_inspector_ip', userIp);
            return userIp;
        } catch (err) {
            console.warn("Could not capture IP address:", err);
            return "Unknown";
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        // This is a UI-level soft lock. The password is intentionally hardcoded for this demo.
        // In a production environment, this would hit an API endpoint.
        if (password === 'GEA-NDT-2026' && name.trim().length > 0 && email.trim().length > 0) {
            sessionStorage.setItem('nd_auth_session', 'active');
            sessionStorage.setItem('nd_inspector_name', name.trim());
            sessionStorage.setItem('nd_inspector_email', email.trim());

            const userIp = await fetchIp();
            logSessionStart(name.trim(), email.trim(), userIp);

            setError(false);
            setIsAuthenticated(true);
        } else {
            setError(true);
            setTimeout(() => setError(false), 2000);
        }
    };

    const handleRequestPasskey = () => {
        const subject = encodeURIComponent("NDT-CHECK PRO Access Request");
        const body = encodeURIComponent(`Hi Oeble,\n\nPlease could you provide the passkey for NDT-CHECK PRO V6.0?\n\nRequested By:\nName: ${name || '[Enter Name]'}\nEmail: ${email || '[Enter Email]'}`);
        window.location.href = `mailto:oeble.huijgen@gea.com?subject=${subject}&body=${body}`;
    };

    if (isWaking) {
        return <div className="h-screen w-screen bg-[#050b14]"></div>;
    }

    if (isAuthenticated) {
        return <>{children}</>;
    }

    return (
        <div className="h-screen w-screen bg-slate-300 flex items-center justify-center p-6 relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none data-grid-layer"></div>
            <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#0000a9]/10 rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#0000a9]/5 rounded-full blur-[120px] pointer-events-none"></div>

            <div className="relative z-10 max-w-md w-full animate-fadeIn">
                <div className="bg-slate-400 border border-slate-500 rounded-none shadow-2xl overflow-hidden backdrop-blur-sm">
                    {/* Header */}
                    <div className="bg-slate-500 border-b border-slate-600 p-8 text-center flex flex-col items-center">
                        <div className="w-16 h-16 bg-[#0000a9] rounded-none flex items-center justify-center shadow-[0_0_20px_rgba(0,0,169,0.3)] mb-6 border border-white/10">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-black text-white uppercase italic tracking-wider mb-2">Internal Access</h1>
                        <p className="text-slate-900/60 font-black text-[10px] uppercase tracking-widest leading-none">Encrypted Neuro-Edge Terminal</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleLogin} className="p-8 space-y-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-slate-700 font-black text-[10px] uppercase tracking-widest mb-2">Inspector Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="J. DOE..."
                                    className="w-full bg-slate-300 border border-slate-500 focus:border-[#0000a9] focus:shadow-[0_0_15px_rgba(0,0,169,0.1)] rounded-none p-4 text-slate-900 font-black text-sm placeholder-slate-500 outline-none transition-all tracking-widest text-center uppercase"
                                    autoFocus
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-slate-700 font-black text-[10px] uppercase tracking-widest mb-2">Corporate Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="user@gea.com"
                                    className="w-full bg-slate-300 border border-slate-500 focus:border-[#0000a9] focus:shadow-[0_0_15px_rgba(0,0,169,0.1)] rounded-none p-4 text-slate-900 font-black text-sm placeholder-slate-500 outline-none transition-all tracking-widest text-center lowercase"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-slate-700 font-black text-[10px] uppercase tracking-widest mb-2">Passkey Authorization</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="ENTER SECURE KEY..."
                                    className={`w-full bg-slate-300 border ${error ? 'border-red-500' : 'border-slate-500 focus:border-[#0000a9]'} rounded-none p-4 text-slate-900 font-black text-sm placeholder-slate-500 outline-none transition-all tracking-widest text-center`}
                                    required
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-[#0000a9] hover:bg-[#000080] text-white font-black uppercase tracking-[0.2em] text-[11px] py-5 rounded-none shadow-xl transition-all active:scale-[0.98] border border-white/5"
                        >
                            Initialize Link
                        </button>

                        <button
                            type="button"
                            onClick={handleRequestPasskey}
                            className="w-full bg-transparent hover:bg-slate-500/20 text-slate-700 hover:text-slate-900 font-black uppercase tracking-[0.1em] text-[10px] py-4 rounded-none transition-all border border-slate-500"
                        >
                            Get Passkey via Email
                        </button>

                        {error && (
                            <p className="text-red-600 font-black text-[10px] uppercase tracking-wider text-center animate-pulse">
                                Access Denied. Invalid Authorization Code.
                            </p>
                        )}
                    </form>

                    {/* Footer */}
                    <div className="bg-slate-500 p-4 text-center border-t border-slate-600 relative">
                        <p className="text-white/60 font-black text-[8px] uppercase tracking-widest">V6.0 • Authorized Personnel Only • GEA Security</p>
                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                            <AdminLogButton />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
